import { createClient, plugins } from '../../../httpClient';
import {
  ApplicationPartType,
  BillingTableItem,
  DebitMemoResponse,
  GetApplicationPartResponse,
  InvoiceResponse,
} from '../../invoice/types';
import { Subscription } from '../../subscriptions/types';
import { PaymentProvider } from '../AuthServiceTypes';
import { GetSubscriptionsResponse, Metrics } from '../types';
import {
  JWTToken,
  LicenseActiveUsersTableData,
  LicenseUsageChartData,
  LicenseUsageTableData,
} from './types';

export const createSelfServiceClient = () => {
  const apiClient = createClient('/api/', plugins.throwOnResponse, plugins.addCookiesToRequest);
  const sessionClient = createClient(
    '/session/',
    plugins.throwOnResponse,
    plugins.addCookiesToRequest,
  );
  const webshopApiClient = createClient(
    '/webshop-api/',
    plugins.addAuthHeader,
    plugins.throwOnResponse,
  );

  const updatePONumber = async (item: BillingTableItem, poNumber: string) => {
    const isInvoice = item.InvoiceNumber.startsWith('INV');
    const endpoint = `billing/${isInvoice ? 'invoices' : 'debitmemos'}/`;
    const url = `${endpoint}${item.Id}`;
    const body = { poNumber };

    const res = await webshopApiClient
      .postJson(url, body)
      .then((res) => res.json<{ success: boolean }>());

    if (!res.success) {
      throw new Error('Could not update PO number');
    }
  };

  const getSubscriptionManagerMetrics = () =>
    webshopApiClient('subscription-manager/metrics').then((res) => res.json<Metrics>());

  const exportData = (recipient: string) =>
    webshopApiClient.postJson('webshop-api/users/me/export', { recipient });

  const getLicenseUsageChartData = (licenseId: string, timeSpan: number) =>
    apiClient(`subscription/${licenseId}/usage/usageChart/${timeSpan}`).then((res) =>
      res.json<LicenseUsageChartData>(),
    );

  const getLicenseUsageTableData = (licenseId: string, timeSpan: number) =>
    apiClient(`subscription/${licenseId}/usage/usageTable/${timeSpan}`).then((res) =>
      res.json<LicenseUsageTableData>(),
    );

  const getLicenseActiveUsersTableData = (licenseId: string) =>
    apiClient(`subscription/${licenseId}/usage/activeUsers/0`).then((res) =>
      res.json<LicenseActiveUsersTableData>(),
    );

  const getAllSubscriptions = () =>
    apiClient('account/subscription/all').then((res) =>
      res.json<{ subscriptions: Subscription[] }>(),
    );

  const getApplicationParts = (type: ApplicationPartType, id: string) =>
    webshopApiClient(`billing/${type}/${id}/application-parts`).then((r) =>
      r.json<GetApplicationPartResponse>(),
    );

  const getInvoices = () =>
    webshopApiClient('billing/invoices').then((res) => res.json<InvoiceResponse>());

  const getDebitMemos = () =>
    webshopApiClient('billing/debitmemos').then((res) => res.json<DebitMemoResponse>());

  const getToken = () =>
    sessionClient(`token`, { method: 'POST' }).then((res) => res.json<JWTToken>());

  const getSubscription = () =>
    apiClient('subscription').then((res) => res.json<GetSubscriptionsResponse>());

  const getPaymentProvider = () =>
    apiClient('account', {}).then((res) => res.json<PaymentProvider | undefined>());

  return {
    exportData,
    getSubscriptionManagerMetrics,
    getLicenseUsageChartData,
    getLicenseUsageTableData,
    getLicenseActiveUsersTableData,
    updatePONumber,
    getToken,
    getAllSubscriptions,
    getApplicationParts,
    getInvoices,
    getDebitMemos,
    getSubscription,
    getPaymentProvider,
  };
};

export type SelfServiceClient = ReturnType<typeof createSelfServiceClient>;

export const selfServiceClient = createSelfServiceClient();
